import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

const SUPPORTED_LANGUAGES = [
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

/**
 * 后台管理语言选择器组件
 * @param {string} currentLang - 当前选中的语言
 * @param {function} onLangChange - 语言变更回调
 * @param {string[]} allowedLanguages - 管理员可管理的语言列表，空数组表示可管理全部
 */
const AdminLanguageSelector = ({ currentLang, onLangChange, allowedLanguages = [] }) => {
  const { t } = useTranslation();
  
  // 如果allowedLanguages为空，则允许所有语言
  const availableLanguages = allowedLanguages.length > 0 
    ? SUPPORTED_LANGUAGES.filter(lang => allowedLanguages.includes(lang.code))
    : SUPPORTED_LANGUAGES;
  
  const currentLangInfo = SUPPORTED_LANGUAGES.find(l => l.code === currentLang) || SUPPORTED_LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="admin-lang-selector">
          <Globe className="w-4 h-4" />
          <span>{currentLangInfo.flag} {currentLangInfo.name}</span>
          <Badge variant="secondary" className="ml-1 text-xs">
            {t('admin.editingLanguage', '编辑语言')}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t('admin.selectEditLanguage', '选择编辑语言版本')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableLanguages.map(lang => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => onLangChange(lang.code)}
            className="gap-2 cursor-pointer"
            data-testid={`admin-lang-${lang.code}`}
          >
            <span>{lang.flag}</span>
            <span className="flex-1">{lang.name}</span>
            {currentLang === lang.code && <Check className="w-4 h-4 text-green-600" />}
          </DropdownMenuItem>
        ))}
        {availableLanguages.length < SUPPORTED_LANGUAGES.length && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-gray-500">
              {t('admin.limitedLanguageAccess', '您只能管理以上语言版本')}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AdminLanguageSelector;
